
import { useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { PuttSituation } from "@/types/golf";

interface PuttingAutoSaveData {
  situationId: number;
  scores: Record<string, number[]>;
  currentPoint: string;
  currentAttempt: number;
  savedAt: string;
}

interface UsePuttingAutoSaveProps {
  situation: PuttSituation;
  scores: Record<string, number[]>;
  currentPoint: string;
  currentAttempt: number;
  setScores: (scores: Record<string, number[]>) => void;
  setCurrentPoint: (point: string) => void;
  setCurrentAttempt: (attempt: number) => void;
}

const STORAGE_KEY = 'putting_exercise_autosave';

export const usePuttingAutoSave = ({
  situation,
  scores,
  currentPoint,
  currentAttempt,
  setScores,
  setCurrentPoint,
  setCurrentAttempt
}: UsePuttingAutoSaveProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('common');

  // Load saved data on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsed: PuttingAutoSaveData = JSON.parse(savedData);
        
        // Only restore if it's the same exercise and has scores
        if (parsed.situationId === situation.id && Object.keys(parsed.scores).length > 0) {
          setScores(parsed.scores);
          setCurrentPoint(parsed.currentPoint);
          setCurrentAttempt(parsed.currentAttempt);
          
          toast({
            title: t('toasts.exerciseRestored.title'),
            description: t('toasts.exerciseRestored.description'),
          });
          
          console.log('Putting exercise data restored:', parsed);
        } else if (parsed.situationId !== situation.id) {
          // Different exercise, clear old data
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error("Error loading saved putting data:", err);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [situation.id]); // Only run on mount and situation change

  // Auto-save whenever scores, currentPoint or currentAttempt change
  useEffect(() => {
    if (Object.keys(scores).length > 0) {
      const dataToSave: PuttingAutoSaveData = {
        situationId: situation.id,
        scores,
        currentPoint,
        currentAttempt,
        savedAt: new Date().toISOString()
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      console.log('Putting exercise data auto-saved:', dataToSave);
    }
  }, [scores, currentPoint, currentAttempt, situation.id]);

  // Clear saved data (call when exercise is completed or cancelled)
  const clearAutoSave = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    console.log('Putting exercise auto-save cleared');
  }, []);

  return { clearAutoSave };
};
